import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Share2, Users2, MousePointerClick, ShieldCheck, Loader2 } from "lucide-react";
import MobileCaptureDialog from "../components/MobileCaptureDialog";
import { setUserId } from "../lib/utm";

/**
 * /my/referrals — the sharer-facing view of who opened their referral links.
 *
 * Identity comes from the `customer_id` httponly cookie set by
 * /api/customers/identify. If the visitor has never identified themselves
 * (i.e. never entered their mobile), we show the MobileCaptureDialog first
 * so they can retroactively claim their referral history (matched by phone).
 */
export default function MyReferrals() {
  const [state, setState] = useState({ loading: true, needsIdentify: false, data: null });
  const [askMobile, setAskMobile] = useState(false);

  const load = () => {
    setState((s) => ({ ...s, loading: true }));
    api
      .get("/customers/me/referrals")
      .then((r) => setState({ loading: false, needsIdentify: false, data: r.data }))
      .catch((err) => {
        if (err?.response?.status === 401) {
          setState({ loading: false, needsIdentify: true, data: null });
          setAskMobile(true);
        } else {
          setState({ loading: false, needsIdentify: false, data: null });
        }
      });
  };

  useEffect(() => { load(); }, []);

  if (state.loading) {
    return (
      <div className="min-h-[60vh] grid place-items-center">
        <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
      </div>
    );
  }

  if (state.needsIdentify) {
    return (
      <div className="min-h-[60vh] grid place-items-center px-6">
        <div className="max-w-md text-center">
          <div className="w-14 h-14 rounded-full bg-black text-white grid place-items-center mx-auto mb-4">
            <Share2 className="w-6 h-6" />
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Your referrals</h1>
          <p className="text-neutral-600 mt-2 text-sm">
            Enter your mobile number to see everyone who opened the links you shared.
          </p>
          <button
            onClick={() => setAskMobile(true)}
            data-testid="my-referrals-open-identify"
            className="mt-6 h-11 px-6 bg-black text-white rounded-lg text-sm font-medium hover:bg-[#0055FF] transition-colors"
          >
            Verify mobile to continue
          </button>
        </div>
        <MobileCaptureDialog
          open={askMobile}
          onOpenChange={setAskMobile}
          product={{
            provider: "Offer Express",
            subtitle: "Match your referral history",
            brand_color: "#0055FF",
          }}
          badge="Match your referral history"
          title="Verify your mobile"
          description="We'll match your number to the referral links you've shared and show everyone who applied through you."
          ctaLabel="View my referrals"
          onCaptured={(data) => {
            if (data?.hitch_user_id) setUserId(data.hitch_user_id);
            setAskMobile(false);
            load();
          }}
        />
      </div>
    );
  }

  const me = state.data?.me || {};
  const summary = state.data?.summary || { clicks: 0, unique_recipients: 0, identified_recipients: 0 };
  const clicks = state.data?.clicks || [];

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-10 py-10">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="badge-mono text-neutral-500 mb-2">Your referrals</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter">Everyone who opened your links</h1>
          <p className="text-neutral-600 mt-2 text-sm">
            Signed in as{" "}
            <span className="font-mono-accent text-[#0055FF]">{me.hitch_user_id}</span>
            {me.phone ? <> · {me.phone}</> : null}
          </p>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
        <SummaryTile icon={<MousePointerClick className="w-4 h-4" />} label="Total clicks" value={summary.clicks} testid="my-ref-clicks" />
        <SummaryTile icon={<Users2 className="w-4 h-4" />} label="Unique recipients" value={summary.unique_recipients} testid="my-ref-recipients" />
        <SummaryTile icon={<ShieldCheck className="w-4 h-4" />} label="Verified recipients" value={summary.identified_recipients} testid="my-ref-verified" />
      </div>

      {/* Recipients list */}
      {clicks.length === 0 ? (
        <div className="bg-white border border-black/10 rounded-xl p-10 text-center">
          <Share2 className="w-8 h-8 mx-auto text-neutral-300 mb-3" />
          <div className="font-display text-lg font-semibold">No opens yet</div>
          <p className="text-neutral-500 text-sm mt-1 max-w-md mx-auto">
            Share a product link with the &ldquo;Share with a friend&rdquo; button. As soon as someone opens it and enters their mobile, they&rsquo;ll show up here.
          </p>
        </div>
      ) : (
        <div className="bg-white border border-black/10 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-black/5 text-[11px] uppercase tracking-[0.15em] text-neutral-500">
            Recipients (newest first)
          </div>
          <ul className="divide-y divide-black/5">
            {clicks.map((c) => (
              <li key={c.id} data-testid={`my-ref-row-${c.id}`} className="px-4 py-3 flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-neutral-100 grid place-items-center flex-shrink-0">
                  {c.recipient_identified ? <ShieldCheck className="w-4 h-4 text-[#00A36C]" /> : <Users2 className="w-4 h-4 text-neutral-400" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium">{c.product_title || "Offer"}</span>
                    {c.product_provider && (
                      <span className="text-[11px] text-neutral-500">· {c.product_provider}</span>
                    )}
                  </div>
                  <div className="text-xs text-neutral-600 mt-0.5">
                    {c.recipient_identified ? (
                      <>
                        <span className="font-medium">{c.recipient_name || "Unnamed"}</span>
                        {c.recipient_phone && <> · {c.recipient_phone}</>}
                        {c.recipient_email && <> · {c.recipient_email}</>}
                      </>
                    ) : (
                      <span className="text-neutral-400">Anonymous visitor — did not enter mobile</span>
                    )}
                  </div>
                </div>
                <div className="text-[11px] text-neutral-500 whitespace-nowrap flex-shrink-0">
                  {c.created_at ? new Date(c.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function SummaryTile({ icon, label, value, testid }) {
  return (
    <div className="bg-white border border-black/10 rounded-xl p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-neutral-500 text-[11px] uppercase tracking-[0.15em]">
        {icon}
        {label}
      </div>
      <div className="font-display text-3xl font-bold mt-2">{value ?? 0}</div>
    </div>
  );
}
